import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { LANGS, type Lang } from "./strings";

const STORAGE_KEY = "admin_lang";

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  // Plain text lookup. Returns the key itself if not found (matches legacy T()).
  t: (key: string) => string;
  // HTML-allowing lookup. Returns string with <em>/<strong>/<br> markup
  // already in the source strings — caller passes it through
  // dangerouslySetInnerHTML.
  tHtml: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function isLang(v: string): v is Lang {
  return v === "fr" || v === "en" || v === "es" || v === "de";
}

function detectInitialLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && isLang(stored)) return stored;
  return "fr";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nContextValue>(() => {
    const table = LANGS[lang] ?? LANGS.fr;
    return {
      lang,
      setLang,
      t: (key) => table[key] ?? LANGS.fr[key] ?? key,
      tHtml: (key) => table[key] ?? LANGS.fr[key] ?? key,
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
